import { Component, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { StorageService } from 'src/app/services/storage.service';
import { calcDeltaUw } from 'src/app/utils/calculation';
import { roundTwoValuePositions } from 'src/app/utils/Constants';
import { ElevatedTransportComponent } from '../elevated-transport/elevated-transport.component';
import { LifetimeSwsAirComponent } from '../lifetime-sws-air/lifetime-sws-air.component';
import { AirSimulationApiService } from 'src/app/services/air-simulation-api.service';
import { WindowPayloadBuilderService } from 'src/app/services/window-payload-builder.service';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import * as Constants from "../../utils/Constants";
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-swsair-simulation',
  templateUrl: './swsair-simulation.component.html',
  styleUrls: ['./swsair-simulation.component.scss'],
  standalone: false,
})
export class SWSAirSimulationComponent implements OnInit {
  shortEdge: number = 1230;
  longEdge: number = 1480;
  minShortEdge: number = 150;
  maxShortEdge: number = 2500;
  minLongEdge: number = 200;
  maxLongEdge: number = 3000;
  private edgeChange$ = new Subject<void>();
  private destroy$ = new Subject<void>();
  lifetimeResult: string = 'not calculated';
  transportResult: string = 'not calculated';

  projectData: any = null;
  lifetimeData: {
    installationPlace: string;
    latitude: number | null;
    longitude: number | null;
  } = {
      installationPlace: '-',
      latitude: null,
      longitude: null
    };

  elevatedTransportData: {
    transport: string;
    transportKey: string;
    maxHeightDiff: number | null;
  } = {
      transport: '-',
      transportKey: '-',
      maxHeightDiff: null
    };

  isLoading = false;
  hasError = false;
  errorMessage = '';


  constructor(private modalController: ModalController, private storageService: StorageService,
    private airSimulationApiService: AirSimulationApiService, private windowPayloadBuilder: WindowPayloadBuilderService,
    private toastService: ToastService, private translate: TranslateService
  ) { }

  async ngOnInit() {
    await this.loadLifetimeData();
    await this.loadElevatedTransportData();
    this.loadProjectData();
    await this.loadAirSimulationData();

    // debounce real-time input changes (500ms)
    this.edgeChange$.pipe(
      debounceTime(500),
      takeUntil(this.destroy$)
    ).subscribe(() => this.performCalculations());

    // auto-trigger on enter if location or transport already saved
    if (this.hasLocationOrTransport()) {
      await this.performCalculations();
    }
  }

  get mappedWindowType(): any {
    const id = this.projectData?.windowtype;
    if (id === undefined || id === null) return null;
    return Constants.data_windows.find((w: any) => w.id === id) ?? null;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ionViewWillEnter() {
    this.loadProjectData();
  }

  async loadProjectData() {
    try {
      const res = await this.storageService.getProject();
      this.projectData = res ? JSON.parse(res) : null;
      console.log('Loaded project data:', this.projectData);
    } catch (error) {
      console.error('Error loading project data:', error);
    }
  }

  async openLifetime() {
    const modal = await this.modalController.create({
      component: LifetimeSwsAirComponent,
      cssClass: 'configuration-modal',
      componentProps: {
        // Pass any data if needed
      }
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm' && data) {
      this.lifetimeData = {
        installationPlace: data.installationPlace,
        latitude: data.latitude,
        longitude: data.longitude
      };

      console.log('Received lifetime data:', this.lifetimeData);
      // await this.loadProjectData();
      await this.performCalculations();
    }
  }

  async openElevatedTransport() {
    const modal = await this.modalController.create({
      component: ElevatedTransportComponent,
      cssClass: 'configuration-modal',
      componentProps: {
        // Pass any data if needed
      }
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm' && data) {
      // await this.loadProjectData();
      this.elevatedTransportData = {
        transport: data.transport,
        transportKey: data.transportKey,
        maxHeightDiff: data.maxHeightDiff
      };
      console.log('Received transport data:', this.elevatedTransportData);
      await this.performCalculations();
    }
  }
  async isValidateInput(): Promise<boolean> {
    const isShortValid = this.shortEdge >= this.minShortEdge && this.shortEdge <= this.maxShortEdge;
    const isLongValid = this.longEdge >= this.minLongEdge && this.longEdge <= this.maxLongEdge;
    const isShortLessThanOrEqualLong = this.shortEdge <= this.longEdge;
    const errors: string[] = [];

    if (!isShortValid) {
      errors.push(
        this.translate.instant('ERROR_SHORT_EDGE', {
          min: this.minShortEdge,
          max: this.maxShortEdge
        })
      );
    }

    if (!isLongValid) {
      errors.push(
        this.translate.instant('ERROR_LONG_EDGE', {
          min: this.minLongEdge,
          max: this.maxLongEdge
        })
      );
    }

    if (!isShortLessThanOrEqualLong) {
      errors.push(this.translate.instant('ERROR_SHORTEDGE_LONGEDGE'));
    }

    if (errors.length > 0) {
      const message = errors.map(e => `${e}. `).join('\n');
      this.errorMessage = message;
      await this.toastService.show(
        this.translate.instant(message),
        3000,
        'bottom'
      );

    }
    const isFormValid = isShortValid && isLongValid && isShortLessThanOrEqualLong;
    return isFormValid
  }

  next() {

    this.storageService.updateProject({
      airSimulationResult: {
        shortEdge: this.shortEdge,
        longEdge: this.longEdge,
        lifetimeResult: this.lifetimeResult,
        transportResult: this.transportResult,
      }
    });
    this.modalController.dismiss({
      lifetimeResult: this.lifetimeResult,
      transportResult: this.transportResult,
    }, 'confirm');
  }
  close() {
    this.modalController.dismiss();
  }

  async loadLifetimeData() {
    try {
      const projectData = await this.storageService.getProject();
      if (projectData) {
        const project = JSON.parse(projectData);
        if (project.lifetime) {
          this.lifetimeData = {
            installationPlace: project.lifetime.installationPlace || '-',
            latitude: project.lifetime.latitude || '-',
            longitude: project.lifetime.longitude || '-'
          };
        }
      }
    } catch (error) {
      console.error('Error loading lifetime data:', error);
    }
  }

  async loadElevatedTransportData() {
    try {
      const projectData = await this.storageService.getProject();
      if (projectData) {
        const project = JSON.parse(projectData);
        if (project.elevatedTransport) {
          this.elevatedTransportData = {
            transport: project.elevatedTransport.transport || '-',
            transportKey: project.elevatedTransport.transportKey || '-',
            maxHeightDiff: project.elevatedTransport.maxHeightDiff || null
          };
        }
      }
    } catch (error) {
      console.error('Error loading transport data:', error);
    }
  }

  async loadAirSimulationData() {
    try {
      const projectData = await this.storageService.getProject();
      if (projectData) {
        const project = JSON.parse(projectData);
        if (project.airSimulationResult) {
          this.shortEdge = project.airSimulationResult.shortEdge ?? 1230;
          this.longEdge = project.airSimulationResult.longEdge ?? 1480;
          // this.lifetimeResult = project.airSimulationResult.lifetimeResult || 'not calculated';
          // this.transportResult = project.airSimulationResult.transportResult || 'not calculated';
        }
      }
    } catch (error) {
      console.error('Error loading air simulation data:', error);
    }
  }

  // GUARD
  public hasLocationOrTransport(): boolean {
    const hasLocation = this.lifetimeData.latitude != null && this.lifetimeData.longitude != null;
    const hasTransport = this.elevatedTransportData.transport !== '-' && !!this.elevatedTransportData.transport;
    return hasLocation || hasTransport;
  }

  get isValidateDisabled(): boolean {
    const edgesEmpty = !this.shortEdge || !this.longEdge;
    return edgesEmpty || !this.isEdgeRangeValid || !this.isShortEdgeLessThanOrEqualToLongEdge || !this.hasLocationOrTransport() || this.isLoading;
  }

  get isEdgeRangeValid(): boolean {
    const isShortValid =
      this.shortEdge >= this.minShortEdge && this.shortEdge <= this.maxShortEdge;

    const isLongValid =
      this.longEdge >= this.minLongEdge && this.longEdge <= this.maxLongEdge;

    return isShortValid && isLongValid;
  }
  get isShortEdgeLessThanOrEqualToLongEdge(): boolean {
    return this.shortEdge != null &&
      this.longEdge != null &&
      this.shortEdge <= this.longEdge;
  }
  // INPUT CHANGE HANDLERS — emit to debounced stream
  onShortEdgeChange() {
    this.edgeChange$.next();
  }
  onLongEdgeChange() {
    this.edgeChange$.next();
  }
  // private saveEdgesToStorage() {
  //   this.storageService.updateProject({
  //     airSimulationResult: {
  //       shortEdge: this.shortEdge,
  //       longEdge: this.longEdge,
  //       lifetimeResult: this.lifetimeResult,
  //       transportResult: this.transportResult,
  //     }
  //   });
  // }

  // API CALL
  async performCalculations() {
    if (!await this.isValidateInput()) return;
    if (!this.hasLocationOrTransport()) return;

    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';

    try {
      const result: any = await this.airSimulationApiService
        .calculateAirSimulation(this.shortEdge, this.longEdge)
        .toPromise();

      this.updateResults(result);
    } catch (error: any) {
      console.error('Calculation error:', error);
      this.hasError = true;
      this.errorMessage = error.message || 'Failed to calculate. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  private updateResults(result: any) {
    console.log('Air simulation result:', result);
    // this.lifetimeResult = result?.lifetimeOk || 'not calculated';
    // this.transportResult = result?.transportOk || 'not calculated';
    this.lifetimeResult = Constants.getRatingLifetimeTextKey(result?.lifetimeOk, false)
    this.transportResult = Constants.getRatingElevatedTransportTextKey(result?.transportOk)
  }
}
